const HEADER_BYTES = 88;

function surfaceError(code, message) {
  return new Error(`CS2RADAR-SURFACE-${code}: ${message}`);
}

function bytesView(value) {
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  throw surfaceError('101', 'PWSF input must be an ArrayBuffer or typed array');
}

function hex(bytes) {
  let value = '';
  for (const byte of bytes) value += byte.toString(16).padStart(2, '0');
  return value.toUpperCase();
}

export function parseTriangleSurfaces(value, expected = {}) {
  const bytes = bytesView(value);
  if (bytes.byteLength < HEADER_BYTES
    || String.fromCharCode(...bytes.subarray(0, 4)) !== 'PWSF') {
    throw surfaceError('101', 'PWSF magic or header is invalid');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const version = view.getUint32(4, true);
  const headerBytes = view.getUint32(8, true);
  if (version !== 1 || headerBytes !== HEADER_BYTES) {
    throw surfaceError('102', `unsupported PWSF ${version}/${headerBytes}`);
  }

  const triangleCount = view.getUint32(12, true);
  const surfaceCount = view.getUint32(16, true);
  const nameBytes = view.getUint32(20, true);
  if (triangleCount !== Number(expected.triangleCount)
    || surfaceCount === 0 || surfaceCount > 0xffff) {
    throw surfaceError('103', 'PWSF geometry counts do not match AWMH');
  }
  const expectedBytes = HEADER_BYTES + nameBytes + triangleCount * 2;
  if (expectedBytes !== bytes.byteLength) {
    throw surfaceError('104', `invalid PWSF length (${bytes.byteLength}/${expectedBytes})`);
  }

  const geometrySha256 = hex(bytes.subarray(24, 56));
  const expectedGeometrySha256 = String(expected.geometrySha256 || '').toUpperCase();
  if (!/^[0-9A-F]{64}$/.test(expectedGeometrySha256)
    || geometrySha256 !== expectedGeometrySha256) {
    throw surfaceError('105', 'PWSF is not bound to the selected AWMH');
  }

  const decoder = new TextDecoder('utf-8', { fatal: true });
  const surfaceNames = new Array(surfaceCount).fill(null);
  let cursor = HEADER_BYTES;
  const namesEnd = HEADER_BYTES + nameBytes;
  try {
    while (cursor < namesEnd) {
      if (namesEnd - cursor < 4) throw surfaceError('106', 'truncated PWSF name record');
      const surfaceId = view.getUint16(cursor, true);
      const nameLength = view.getUint16(cursor + 2, true);
      cursor += 4;
      if (surfaceId >= surfaceCount || surfaceNames[surfaceId] !== null
        || nameLength === 0 || cursor + nameLength > namesEnd) {
        throw surfaceError('106', 'invalid PWSF name table');
      }
      surfaceNames[surfaceId] = decoder.decode(bytes.subarray(cursor, cursor + nameLength));
      cursor += nameLength;
    }
  } catch (error) {
    if (String(error?.message || error).startsWith('CS2RADAR-SURFACE-106')) throw error;
    throw surfaceError('106', 'PWSF name table is not valid UTF-8');
  }
  if (cursor !== namesEnd || surfaceNames.some((name) => !name)) {
    throw surfaceError('106', 'incomplete PWSF name table');
  }

  const groups = [];
  let groupSurface = -1;
  let groupTriangleStart = 0;
  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const surfaceId = view.getUint16(namesEnd + triangle * 2, true);
    if (surfaceId >= surfaceCount) {
      throw surfaceError('107', 'PWSF triangle surface id is out of range');
    }
    if (triangle === 0) {
      groupSurface = surfaceId;
      continue;
    }
    if (surfaceId !== groupSurface) {
      groups.push({
        start: groupTriangleStart * 3,
        count: (triangle - groupTriangleStart) * 3,
        materialIndex: groupSurface,
      });
      groupSurface = surfaceId;
      groupTriangleStart = triangle;
    }
  }
  if (triangleCount) {
    groups.push({
      start: groupTriangleStart * 3,
      count: (triangleCount - groupTriangleStart) * 3,
      materialIndex: groupSurface,
    });
  }

  return {
    version,
    triangleCount,
    surfaceCount,
    geometrySha256,
    surfacePropertiesSha256: hex(bytes.subarray(56, 88)),
    surfaceNames,
    groups,
  };
}

export function applyTriangleSurfaceGroups(geometry, triangleSurfaceData) {
  if (!geometry?.clearGroups || !geometry?.addGroup) {
    throw surfaceError('108', 'target geometry does not support groups');
  }
  geometry.clearGroups();
  for (const group of triangleSurfaceData?.groups || []) {
    geometry.addGroup(group.start, group.count, group.materialIndex);
  }
  return geometry;
}
